import { CONFIG } from "../helper/config";
import { UserConfigModal } from "../modal/notification";


/**
 * Constructs request options for API calls based on the specified method, authentication token, and optional parameters.
 *
 * @param {string} requestMethod - The HTTP method for the request (e.g., GET, POST, DELETE).
 * @param {string} auth_token - The authentication token to include in the request headers.
 * @param {any} [bodyParams=''] - The body parameters to include in the request for methods that support a body (e.g., POST).
 * @param {UserConfigModal} [user_config_keys] - Optional user configuration keys used to augment request headers.
 * @returns {object} An object representing the request options, including method, headers, and body if applicable.
 */

export function getRequestOptions(requestMethod: string, auth_token:string, bodyParams: any = '', user_config_keys?: UserConfigModal) {
  let requestOptions;
  if (requestMethod === CONFIG.API.REQUESTMETHOD.GET || (requestMethod === CONFIG.API.REQUESTMETHOD.DELETE && bodyParams === '')) {
    requestOptions = {
      method: requestMethod,
      headers: (user_config_keys) ? getHeaders(user_config_keys, auth_token) :{ 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + auth_token },
      redirect: 'follow'
    };
  } else {
    requestOptions = {
      method: requestMethod,
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + auth_token },
      body: bodyParams,
      redirect: 'follow'
    };
  }

  return requestOptions;
}

/**
 * Constructs headers for API calls, including Content-Type and Authorization headers.
 *
 * @param {UserConfigModal} user_config_keys - User configuration keys used to augment request headers.
 * @param {string} auth_token - The authentication token to include in the request headers.
 * @returns {object} An object representing the request headers.
 */
function getHeaders(user_config_keys: UserConfigModal, auth_token:string){
  let content_type = 'application/json';
  return {'Content-Type': content_type, 'Authorization': 'Bearer ' + auth_token,
  'userid': user_config_keys.user_id, 'channelid': user_config_keys.channelId,
  'teamid': user_config_keys.team_id, 'tenantid': user_config_keys.tenant_id
 };
}

/**
 * Handles a CSV response by downloading it as a file.
 *
 * @param {Response} response - The response object from the fetch API.
 * @returns {Promise<string>} A promise that resolves with a success message if CSV is downloaded,
 * or rejects with an error if the response is not a CSV or if download fails.
 */
export function handleCSVResponse(response: Response) {
  const contentType = response.headers.get('Content-Type');
  const desc = response.headers.get('content-disposition');
  const file = (desc?.split('"')[desc.split('"').findIndex(part => part.includes("filename=")) + 1] ?? "").trim();

  if (contentType?.includes('text/csv')) {
    return response.blob().then((blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file); 
      document.body.appendChild(link);
      link.click();
      link.remove();
      return Promise.resolve("CSV Downloaded");
    }).catch((error) => {
      return Promise.reject(new Error(error));
    });
  } else {
    return Promise.reject(new Error('Response is not a CSV file.'));
  }
}

/**
 * Handles a JSON response from the API.
 * Parses the response text into JSON and checks if the response is successful.
 * If the response is not successful, it retrieves and throws an appropriate error message.
 *
 * @param {Response} response - The response from the API.
 * @returns {Promise<any>} A promise that resolves to the parsed JSON data if the response is successful,
 * or rejects with an error message if the response is not successful.
 */
export function handleJSONResponse(response: Response) {
  return response.text().then((text: string) => {
    const data = text && JSON.parse(text);
    if (!response.ok) {
      let error = getErrorMessage(data, response.status, response.statusText);
      const err = new Error(error.detail || error);
      (err as any).info = error;   // keep original object
      return Promise.reject(err);
    }
    return data;
  });
}

/**
 * Handles an API response by determining its content type and delegating to the appropriate handler.
 * If the response has a status of 502, 500, or 404 and is not OK, it rejects with an error.
 * For CSV content, it delegates to handleCSVResponse.
 * For JSON content, it delegates to handleJSONResponse.
 *
 * @param {Response} response - The response object from the fetch API.
 * @returns {Promise<any>} A promise that resolves or rejects based on the response content type and status.
 */
export function handleResponse(response: any) {
  if ((response.status === 502 || response.status === 500 || response.status === 404) && !response.ok) {
    return Promise.reject(new Error(response.statusText));
  } else {
    const contentType = response.headers.get('Content-Type');
    if (contentType?.includes('text/csv')) {
      return handleCSVResponse(response);
    } else {
      return handleJSONResponse(response);
    }
  }
}

/**
 * Handles an HTML response from the API.
 * If the response has a status of 502, 500, or 404 and is not OK, it rejects with an error.
 * Otherwise, it returns the response text.
 *
 * @param {Response} response - The response object from the fetch API.
 * @returns {Promise<string>} A promise that resolves or rejects based on the response status.
 */
export function handleHTMLResponse(response: any) {
  if ((response.status === 502 || response.status === 500 || response.status === 404) && !response.ok) {
    return Promise.reject(new Error(response.statusText));
  } else {
    return response.text().then(function (text: any) {
      let data = text;
      if (!response.ok) {
        data = text && JSON.parse(text);
        let error = getErrorMessage(data, response.status, response.statusText);
        return Promise.reject(new Error(error));
      }
      return data;
    });
  }
}

/**
 * Retrieves an appropriate error message based on response data and status.
 *
 * @param {any} data - The data object returned in the response, which may contain error details.
 * @param {any} response_status - The HTTP status code of the response.
 * @param {any} response_statustext - The status text associated with the response.
 * @returns {string} A string representing the error message, derived from the response data or status.
 *
 * Handles specific status codes by providing custom error messages:
 *   - 401: Uses detailed message if available, otherwise uses a default forbidden message.
 *   - 403: Returns a predefined access revoked message.
 *   - 400: Refines the error message using a specialized function for 400 responses.
 * For all other statuses, defaults to using the message from the response data or status text.
 */

function getErrorMessage(data : any, response_status : any, response_statustext: any) {
  let error = (data?.message) || response_statustext;
  switch (response_status) {
    case 401:
      error = data.detail ? data.detail : CONFIG.API.ERRORMESSAGE.FORBIDDEN;
      break;

    case 403:
      console.error("Access revoked error:", data);
      error= data.detail ? data : CONFIG.ACCESSREVOKED;
      break;
    
    case 400:
      error = refactor400RespnseErrorMessage(data, error)
      break;

    default:
      break;
  }

  return error;
}

/**
 * Refines the error message for 400 responses by looking for specific
 * keys in the response data and returning the value associated with
 * that key. If no relevant key is found, returns the original error
 * message.
 * @param {object|string} data - The response data object or string.
 * @param {string} error - The original error message.
 * @returns {string} The refined error message.
 */
export function refactor400RespnseErrorMessage(data:any, error:any) {
  if(data.expiry || data.demo){
    error = data;
  }else if(data.message){
    error = data.message;
  }else if(data.error){
    error = data.error;
  }else if(data.msg){
    error = data.msg;
  }else if (typeof data === 'string' && data.trim() !== '') {
    error = data;
  }
  return error;
}

/**
 * Logs the error and rejects the promise with a normalized error object.
 *
 * @param {Error & { info?: any }} error - The error to be handled, possibly containing extra info.
 * @returns {Promise<never>} A rejected promise with a new `Error` containing the same message and info.
 */
export function handleError(error: any) {
  console.log("error :: ", error, "error :: message ", error.message, "error info :: ",error.info)
  const err = new Error(error.message);
  (err as any).info = error.info;   // keep original object
  return Promise.reject(err);
}
